import {
  BundleCompositionDictionary,
  getBundleCompositionDictionary,
} from 'constants/dynamic-bundle';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { PetType } from 'constants/pet-type';

export interface Bundle {
  id: string;
  name: string;
  hasPetName: boolean;
  dictionary: BundleCompositionDictionary;
}

interface DynamicBundleStore {
  bundles: Bundle[];
  withPetNamesCounter: number;
  withoutPetNamesCounter: number;
  addBundle: (bundle: Bundle) => void;
  removeBundle: (bundleId: string) => void;
  flush: () => void;
}

/**
 * Stores bundle names and their counters
 */
export const createStore = ({ name }: { name: string }) => {
  return create<DynamicBundleStore>()(
    devtools(
      persist(
        (set) => ({
          bundles: [],
          withPetNamesCounter: 0,
          withoutPetNamesCounter: 0,
          addBundle: (bundle) => {
            set((store) => {
              if (bundle.hasPetName) {
                return {
                  bundles: [...store.bundles, bundle],
                  withPetNamesCounter: store.withPetNamesCounter + 1,
                };
              }
              return {
                bundles: [...store.bundles, bundle],
                withoutPetNamesCounter: store.withoutPetNamesCounter + 1,
              };
            });
          },
          // we do not change names counters on remove
          removeBundle: (bundleId) => {
            set((store) => ({ bundles: store.bundles.filter((item) => item.id !== bundleId) }));
          },
          flush: () => set({ bundles: [], withPetNamesCounter: 0, withoutPetNamesCounter: 0 }),
        }),
        {
          name,
          version: 1,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            bundles: state.bundles,
            withPetNamesCounter: state.withPetNamesCounter,
            withoutPetNamesCounter: state.withoutPetNamesCounter,
          }),
          // rare case when we added items to the cart having previous version of Dynamic bundle in the store.
          migrate: (persistedState) => {
            const state = persistedState as DynamicBundleStore;
            return {
              ...state,
              bundles: state.bundles.map((bundle) => ({
                ...bundle,
                dictionary:
                  bundle.dictionary ?? getBundleCompositionDictionary({ petType: PetType.Cat }),
              })),
            };
          },
        }
      ),
      { name: 'DynamicBundleStore', enabled: process.env.PD_ENV === 'staging' }
    )
  );
};

export const useDynamicBundleStore = createStore({ name: 'cart:dynamic-bundles' });
